/**
 * ItemCreationProvenanceControl
 *
 * Item-detail-view header control for Feature 3 (AI Provenance Indicators).
 * Renders the ItemCreationProvenanceBadge for the item's creation provenance
 * (nothing for 'human'), plus an "Approve" button when the item is
 * 'ai_draft' and the current user holds items.approve_ai_provenance.
 *
 * Self-contained like ClassificationStatusBanner (only an item id is
 * required; everything else is pulled from stores internally) and mirrors
 * its post-RPC refresh approach — on a successful approve_ai_item call, the
 * item is re-fetched from Supabase (loadItemById) rather than patched via
 * itemsStore.updateItem's direct client write, since the ai_draft ->
 * ai_approved transition is an authority transition that must go through
 * the RPC (Build Contract §2.3a).
 */

import { useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { approveAiItemRpc } from '../../../lib/itemProvenanceRpc';
import { ItemCreationProvenanceBadge } from '../item-list/components/ItemCreationProvenanceBadge';
import { resolveAuthorship } from '../../../types/prioritization/creationProvenance';
import { ReverseActionButton } from '../../reversals/ReverseActionButton';
import { unreviewAiItem } from '../../../lib/reversals/reversalsService';
import { MARK_NOT_REVIEWED_LABEL, MARK_REVIEWED_LABEL, MARKING_REVIEWED_LABEL } from '../../../lib/items/reviewVocabulary';

interface ItemCreationProvenanceControlProps {
  itemId: string;
}

export function ItemCreationProvenanceControl({ itemId }: ItemCreationProvenanceControlProps) {
  const item = useItemsStore((s) => s.getItemById(itemId));
  const loadItemById = useItemsStore((s) => s.loadItemById);
  const userId = useUserStore((s) => s.userId);
  const addNotification = useUIStore((s) => s.addNotification);
  const can = useUserStore((s) => s.can);
  // can() reads the store, not React state: follow permissionsLoaded so this redraws when rights arrive.
  useUserStore((s) => s.permissionsLoaded);
  const [approving, setApproving] = useState(false);

  const authorship = resolveAuthorship({
    source: item?.creationSource,
    reviewStatus: item?.creationReviewStatus,
    provenance: item?.creationProvenance,
  });
  if (authorship.source === 'human' && authorship.reviewStatus === 'none') return null;

  const canApprove = can('items.approve_ai_provenance');
  const showMarkReviewed =
    authorship.source !== 'human' && authorship.reviewStatus === 'none' && canApprove;
  const showMarkNotReviewed =
    authorship.source !== 'human' && authorship.reviewStatus === 'human_reviewed' && canApprove;

  async function handleApprove() {
    if (!userId || approving) return;
    setApproving(true);
    const result = await approveAiItemRpc(itemId, userId);
    setApproving(false);

    if (result.success) {
      await loadItemById(itemId);
      addNotification({
        type: 'success',
        title: 'Marked as reviewed',
        message: 'This AI-authored item is now human-reviewed.',
        duration: 3000,
      });
    } else {
      addNotification({
        type: 'error',
        title: 'Could not mark as reviewed',
        message: result.error ?? 'Could not update review status.',
        duration: 5000,
      });
    }
  }

  return (
    <div className="flex items-center gap-1.5 flex-shrink-0">
      <ItemCreationProvenanceBadge
        source={authorship.source}
        reviewStatus={authorship.reviewStatus}
        provenance={item?.creationProvenance}
      />
      {showMarkReviewed && (
        <button
          type="button"
          onClick={handleApprove}
          disabled={approving}
          title="Optionally mark this AI-authored item as human-reviewed. Review is never required."
          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-[#3A7D56] text-white hover:bg-[#2f6647] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {approving ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
          {approving ? MARKING_REVIEWED_LABEL : MARK_REVIEWED_LABEL}
        </button>
      )}
      {showMarkNotReviewed && (
        <ReverseActionButton
          label={MARK_NOT_REVIEWED_LABEL}
          confirmTitle={MARK_NOT_REVIEWED_LABEL}
          explain="This will take back the human review of this AI-authored item, returning it to AI-drafted status."
          run={(reason) => unreviewAiItem(itemId, reason)}
          onDone={() => loadItemById(itemId)}
          variant="ghost"
          size="sm"
          className="!px-2 !py-1 !text-xs"
        />
      )}
    </div>
  );
}
